import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, getDocs, where, Timestamp } from "@firebase/firestore";
import { app } from '../../firebase.js';
import Table from 'react-bootstrap/Table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import LeftBar from '../../Components/LeftBar'
import Dashnav from '../../Components/Dashnav'

function ConvBySource() {
    const db = getFirestore(app);
    const [convData, setConvData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            let q = collection(db, 'users');

            // Add date filtering if both dates are selected
            if (startDate && endDate) {
                const startTimestamp = Timestamp.fromDate(new Date(startDate));
                const endTimestamp = Timestamp.fromDate(new Date(endDate));
                q = query(q, where('appDate', '>=', startTimestamp), where('appDate', '<=', endTimestamp));
            } 

            const querySnapshot = await getDocs(q);

            const counts = {};
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                const source = data.hear ? data.hear.toLowerCase() : 'unknown';
                const status = data.userStatus || 'Unknown';

                if (!counts[source]) {
                    counts[source] = { 
                        applicants: 0,
                        active: 0, 
                        graduated: 0, 
                        neverJoined: 0, 
                        deferred: 0,
                        expelled: 0,
                        dropped: 0
                    };
                }

                if (status === 'inactive') counts[source].applicants += 1;
                else if (status === 'Student') counts[source].active += 1;
                else if (status === 'Completed') counts[source].graduated += 1;
                else if (status === 'Did Not Join') counts[source].neverJoined += 1;
                else if (status === 'Deferred') counts[source].deferred += 1;
                else if (status === 'Expelled') counts[source].expelled += 1;
                else if (status === 'Dropped') counts[source].dropped += 1;
            });

            const formattedData = Object.keys(counts).map((key) => ({
                source: key,
                applicants: counts[key].applicants,
                active: counts[key].active,
                graduated: counts[key].graduated,
                neverJoined: counts[key].neverJoined,
                deferred: counts[key].deferred,
                expelled: counts[key].expelled,
                dropped: counts[key].dropped
            }));

            const sortedData = formattedData.sort(
                (a, b) => (
                    b.applicants + b.active + b.graduated + b.neverJoined + b.deferred + b.expelled + b.dropped
                ) - (
                    a.applicants + a.active + a.graduated + a.neverJoined + a.deferred + a.expelled + a.dropped
                )
            );

            setConvData(sortedData);
            setLoading(false);
        };

        fetchData();
    }, [db, startDate, endDate]);

    return (
        <div className='mainContainer'>
            <LeftBar />
            <div className='theMainContainer'>
                <Dashnav />
                <div className="container mt-4">
                    <h4>Students Breakdown by Source</h4>

                    {/* Date Filter Inputs */}
                    <div className="mb-3">
                        <label className="me-2">From:</label>
                        <input
                            type="date"
                            className="form-control d-inline-block w-auto me-3"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                        />
                        <label className="me-2">To:</label>
                        <input
                            type="date"
                            className="form-control d-inline-block w-auto"
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                        />
                    </div>

                    {loading ? (
                        <p>Loading...</p>
                    ) : (
                        <>
                            <div style={{ width: '100%', height: 400 }}>
                                <ResponsiveContainer>
                                    <BarChart data={convData.slice(0, 8)} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="source" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="applicants" fill="#6f42c1" name="Current Applicants" />
                                        <Bar dataKey="active" fill="#8884d8" name="Active Students" />
                                        <Bar dataKey="graduated" fill="#82ca9d" name="Graduated" />
                                        <Bar dataKey="neverJoined" fill="#ffc658" name="Never Joined" />
                                        <Bar dataKey="deferred" fill="#ff8042" name="Deferred" />
                                        <Bar dataKey="expelled" fill="#ff0000" name="Expelled" />
                                        <Bar dataKey="dropped" fill="#a4de6c" name="Dropped" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <Table striped bordered hover className="mt-4">
                                <thead>
                                    <tr>
                                        <th>Source</th>
                                        <th>Current Applicants</th>
                                        <th>Active Students</th>
                                        <th>Graduated</th>
                                        <th>Never Joined</th>
                                        <th>Deferred</th>
                                        <th>Expelled</th>
                                        <th>Dropped</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {convData.map((row, idx) => (
                                        <tr key={idx}>
                                            <td>{row.source}</td>
                                            <td>{row.applicants}</td>
                                            <td>{row.active}</td>
                                            <td>{row.graduated}</td>
                                            <td>{row.neverJoined}</td>
                                            <td>{row.deferred}</td>
                                            <td>{row.expelled}</td>
                                            <td>{row.dropped}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ConvBySource;